import { Level } from '../config/levels';

// Your exact endpoint
const DAILY_LEVELS_ENDPOINT = 'https://api.noway.lol/v1/daily';

/**
 * Fetches the daily levels from the remote API
 */
export async function fetchDailyLevels(): Promise<Level[]> {
  try {
    const response = await fetch(DAILY_LEVELS_ENDPOINT, {
      method: 'GET', // Changed from POST to GET
      headers: {
        'Content-Type': 'application/json',
      },
      // Remove body since GET requests don't have a body
      // Add this to help with potential CORS issues during development
      mode: 'cors',
      credentials: 'omit'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch daily levels: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('API Response:', data); // Debug log to see the response
    
    // Check that we have the expected data structure
    if (!data || !Array.isArray(data.levels)) {
      throw new Error('Invalid response format: Expected levels array');
    }
    
    return data.levels
      .map((level: any) => {
        const mappedLevel: Level = {
          id: level.id,
          sentence: level.content || level.sentence
        };
        
        // Only add difficulty if it exists in the response
        if (level.difficulty) {
          mappedLevel.difficulty = level.difficulty;
        }
        
        return mappedLevel;
      })
      .sort((a: any, b: any) => a.id - b.id);
  } catch (error) {
    console.error('Error fetching daily levels:', error);
    // Include more debugging information in the error
    if (error instanceof Error) {
      console.error('Error details:', error.message);
    }
    throw error;
  }
}

/**
 * Fetches levels with proper retry and error handling
 * This would be used when the feature flag is set to true
 */
export async function getDailyLevelsWithRetry(maxRetries = 2): Promise<Level[]> {
  let retries = 0;
  let lastError = new Error("Unknown error occurred");
  
  while (retries <= maxRetries) {
    try {
      // Add exponential backoff for retries
      if (retries > 0) {
        const backoffTime = Math.pow(2, retries) * 500; // 1s, 2s, 4s...
        await new Promise(resolve => setTimeout(resolve, backoffTime));
      }
      
      return await fetchDailyLevels();
    } catch (error) {
      lastError = error as Error;
      retries++;
      
      // Don't retry certain errors
      if (error instanceof Error &&
          (error.message.includes('authenticate') || 
           error.message.includes('No daily levels'))) {
        break;
      }
    }
  }
  
  throw new Error(`Failed to fetch daily levels after ${maxRetries} retries: ${lastError.message}`);
}

/**
 * Initializes and provides caching for daily levels
 * Uses sessionStorage to prevent refetching during a session
 * Falls back to hardcoded levels if API request fails
 */
export async function getAndCacheDailyLevels(): Promise<Level[]> {
  // Check if we have cached levels for today
  const today = new Date().toISOString().split('T')[0];
  const cachedKey = `daily_levels_${today}`;
  
  const cachedLevels = sessionStorage.getItem(cachedKey);
  if (cachedLevels) {
    try {
      const parsedLevels = JSON.parse(cachedLevels);
      return parsedLevels;
    } catch (e) {
      // Invalid cache, proceed to fetch
      console.warn('Invalid cached levels, refetching');
    }
  }
  
  // If no cache or invalid, fetch new levels
  try {
    const levels = await getDailyLevelsWithRetry();
    
    // Cache the levels for this session
    sessionStorage.setItem(cachedKey, JSON.stringify(levels));
    
    return levels;
  } catch (error) {
    console.error('Failed to fetch and cache daily levels:', error);
    
    // Import fallback levels
    const { gameLevels } = await import('../config/levels');
    console.log('Falling back to hardcoded levels');
    
    return gameLevels;
  }
}